import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import MultiSelectDropdown from "../formComponents/MultiSelectDropdown";
import { leadApi, leadSetupApi } from "../../pages/services/leadService";
import LeadForm from "./LeadForm";
import LeadMessagesPanel from "./LeadMessagesPanel";
import { BulkMessageDialog, BulkStatusDialog } from "./LeadBulkActions";
import { useTablePreferences } from "../utils/useTablePreferences";
import { MESSAGE_CHANNEL, StatusPill, errorText, fmtDateTime, fmtDue, isOverdue, sourceLabel, useCampaignOptions, useLeadMeta, FiltersToggle } from "./leadUtils";
import "../../styles/Leads.css";

// Every lead, filterable. The filters live in the address bar, so a dashboard
// number opens exactly its leads and a filtered view can be bookmarked.
// Rows per page — the choice is remembered (useTablePreferences), like the other lists.
const PAGE_SIZES = [10, 20, 50, 100, 500];
const FILTER_KEYS = ["campaignIds", "courseIds", "statuses", "groups", "followUp", "automation", "search", "from", "to", "sort",
  "demoSessionIds", "stages", "adNames", "sources", "messages"];

const LeadList = () => {
  const navigate = useNavigate();
  const meta = useLeadMeta();
  const campaignOptions = useCampaignOptions();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);     // phones: the filters folded away
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const [options, setOptions] = useState({ stages: [], adNames: [], sources: [], demoSessions: [] });
  useEffect(() => { leadApi.filterOptions().then(setOptions).catch(() => {}); }, []);
  const page = Math.max(1, parseInt(params.get("page"), 10) || 1);
  const { rowsPerPage: storedRows, setRowsPerPage } = useTablePreferences("leadListPrefs", 20);
  const rowsPerPage = PAGE_SIZES.includes(Number(storedRows)) ? Number(storedRows) : 20;
  // Rows whose notifications are open (the accordion), and the notifications
  // loaded so far — fetched in one request for however many rows are opened.
  const [open, setOpen] = useState(() => new Set());
  const [messages, setMessages] = useState({});        // leadId → { sent, upcoming, notSent } | { error }
  const loadMessages = useCallback((ids) => {
    const missing = ids.filter((id) => !messages[id]);
    if (!missing.length) return;
    leadApi.messagesBatch(missing)
      .then((byLead) => setMessages((prev) => ({ ...prev, ...byLead })))
      .catch((e) => setMessages((prev) => ({ ...prev, ...Object.fromEntries(missing.map((id) => [id, { error: errorText(e) }])) })));
  }, [messages]);
  const toggle = (id) => {
    const opening = !open.has(id);
    setOpen((prev) => { const next = new Set(prev); if (opening) next.add(id); else next.delete(id); return next; });
    if (opening) loadMessages([id]);
  };
  const allOpen = rows.length > 0 && rows.every((l) => open.has(l._id));
  const toggleAll = () => {
    if (allOpen) { setOpen(new Set()); return; }
    setOpen(new Set(rows.map((l) => l._id)));
    loadMessages(rows.map((l) => l._id));
  };

  const filters = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""])), [params]);

  // Selected leads, kept across pages of the same filters: id → { _id, fullName, courseId, courseTitle, status }.
  const [selected, setSelected] = useState(() => new Map());
  const [exporting, setExporting] = useState(false);
  // "Export contacts": the selected leads if any are ticked, else every lead the filters match.
  const exportContacts = async () => {
    setExporting(true);
    try {
      const { blob, name } = await leadApi.exportContacts(filters, selected.size ? [...selected.keys()] : null);
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setExporting(false);
    }
  };
  const [bulk, setBulk] = useState(null);              // "status" | "message"
  const [selectingAll, setSelectingAll] = useState(false);
  // A new filter starts a new selection; turning pages does not.
  const filterKey = JSON.stringify(filters);
  useEffect(() => { setSelected(new Map()); }, [filterKey]);
  const selectedLeads = useMemo(() => [...selected.values()], [selected]);
  const pick1 = (l) => ({ _id: l._id, fullName: l.fullName, courseId: l.courseId, courseTitle: l.courseTitle, status: l.status });
  const toggleSelect = (l) => setSelected((prev) => { const next = new Map(prev); if (next.has(l._id)) next.delete(l._id); else next.set(l._id, pick1(l)); return next; });
  const pageSelected = rows.length > 0 && rows.every((l) => selected.has(l._id));
  const togglePage = () => setSelected((prev) => {
    const next = new Map(prev);
    rows.forEach((l) => (pageSelected ? next.delete(l._id) : next.set(l._id, pick1(l))));
    return next;
  });
  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const all = await leadApi.ids(filters);
      setSelected(new Map(all.map((l) => [String(l._id), pick1({ ...l, _id: String(l._id) })])));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSelectingAll(false);
    }
  };
  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    next.delete("page");
    setParams(next);
  };

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    leadApi.list({ ...filters, page, limit: rowsPerPage })
      .then((r) => { setRows(r.data); setTotal(r.total); setMessages({}); setOpen(new Set()); })
      .catch((e) => setError(errorText(e)))
      .finally(() => setLoading(false));
  }, [filters, page, rowsPerPage]);
  useEffect(() => { load(); }, [load]);

  // The search box filters after a short pause, not on every key.
  useEffect(() => {
    const t = setTimeout(() => { if (search !== (params.get("search") || "")) setFilter("search", search.trim()); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const statusOptions = (meta?.statuses || []).map((s) => ({ value: s.key, label: s.label }));
  const demoOptions = [{ value: "none", label: "Unassigned" }, ...options.demoSessions.map((d) => ({ value: d._id, label: `${fmtDateTime(d.startsAt)}${d.title ? ` · ${d.title}` : ""}` }))];
  const stageOptions = options.stages.map((x) => ({ value: x, label: x }));
  const adOptions = options.adNames.map((x) => ({ value: x, label: x }));
  const sourceOptions = options.sources.map((x) => ({ value: x, label: sourceLabel(x) }));
  const pick = (opts, csv) => { const set = new Set(csv.split(",").filter(Boolean)); return opts.filter((o) => set.has(o.value)); };
  const groupLabel = { active: "Active", passive: "Passive", later: "Interested for later", dead: "Dead", converted: "Converted" };

  const openAdd = async () => {
    setCampaigns(await leadSetupApi.campaigns().catch(() => []));
    setAdding(true);
  };

  const pages = Math.max(1, Math.ceil(total / rowsPerPage));
  // The list's filters (without its page), carried to a lead's page so its
  // Previous / Next walk this same list and "← Leads" comes back to it.
  const listQuery = useMemo(() => { const q = new URLSearchParams(params); q.delete("page"); return q.toString(); }, [params]);
  const goTo = (p) => { const n = new URLSearchParams(params); if (p > 1) n.set("page", String(p)); else n.delete("page"); setParams(n); };
  const changeRows = (size) => { setRowsPerPage(size); goTo(1); };
  const anyFilter = FILTER_KEYS.some((k) => k !== "sort" && filters[k]);

  return (
    <div className="ld-page">
      <div className="ld-head">
        <div>
          <h1 className="ld-title">Leads</h1>
          <p className="ld-subtitle">{total.toLocaleString("en-IN")} lead{total === 1 ? "" : "s"}{filters.groups ? ` · ${filters.groups.split(",").map((g) => groupLabel[g] || g).join(", ")}` : ""}</p>
        </div>
        <div className="ld-head-actions">
          <Link className="ld-btn" to="/leads">Dashboard</Link>
          <Link className="ld-btn" to="/leads/notifications">Notification log</Link>
          <Link className="ld-btn" to="/leads/registrations">Registrations</Link>
          <button type="button" className="ld-btn" onClick={exportContacts} disabled={exporting || (!total && !selected.size)}
            title="Download name, mobile, alternative mobile, email, course and status as a CSV file">
            {exporting ? "Exporting…" : selected.size ? `⤓ Export ${selected.size.toLocaleString("en-IN")} selected` : `⤓ Export contacts (${total.toLocaleString("en-IN")})`}
          </button>
          <button type="button" className="ld-btn ld-btn-primary" onClick={openAdd}>+ Add lead</button>
        </div>
      </div>

      <FiltersToggle open={filtersOpen} onToggle={() => setFiltersOpen((o) => !o)} active={FILTER_KEYS.filter((k) => k !== "sort" && filters[k]).length} />
      <div className={`ld-filters${filtersOpen ? " is-open" : ""}`}>
        <div className="ld-filter" style={{ minWidth: 240 }}>
          <span className="ld-filter-label">Search</span>
          <input className="ld-input" placeholder="Name, mobile, email, city" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="ld-filter">
          <span className="ld-filter-label">Campaign</span>
          <MultiSelectDropdown options={campaignOptions} value={pick(campaignOptions, filters.campaignIds)} onChange={(v) => setFilter("campaignIds", v.map((x) => x.value).join(","))} placeholder="All campaigns" showCheckboxes />
        </div>
        <div className="ld-filter">
          <span className="ld-filter-label">Status</span>
          <MultiSelectDropdown options={statusOptions} value={pick(statusOptions, filters.statuses)} onChange={(v) => setFilter("statuses", v.map((x) => x.value).join(","))} placeholder="All statuses" showCheckboxes />
        </div>
        <div className="ld-filter">
          <span className="ld-filter-label">Demo</span>
          <MultiSelectDropdown options={demoOptions} value={pick(demoOptions, filters.demoSessionIds)} onChange={(v) => setFilter("demoSessionIds", v.map((x) => x.value).join(","))} placeholder="Any demo" showCheckboxes />
        </div>
        {stageOptions.length > 0 && (
          <div className="ld-filter">
            <span className="ld-filter-label">Stage</span>
            <MultiSelectDropdown options={stageOptions} value={pick(stageOptions, filters.stages)} onChange={(v) => setFilter("stages", v.map((x) => x.value).join(","))} placeholder="All stages" showCheckboxes />
          </div>
        )}
        {adOptions.length > 0 && (
          <div className="ld-filter">
            <span className="ld-filter-label">Ad</span>
            <MultiSelectDropdown options={adOptions} value={pick(adOptions, filters.adNames)} onChange={(v) => setFilter("adNames", v.map((x) => x.value).join(","))} placeholder="All ads" showCheckboxes />
          </div>
        )}
        {sourceOptions.length > 1 && (
          <div className="ld-filter">
            <span className="ld-filter-label">Source</span>
            <MultiSelectDropdown options={sourceOptions} value={pick(sourceOptions, filters.sources)} onChange={(v) => setFilter("sources", v.map((x) => x.value).join(","))} placeholder="All sources" showCheckboxes />
          </div>
        )}
        <div className="ld-filter" style={{ minWidth: 160 }}>
          <span className="ld-filter-label">Follow-up</span>
          <select className="ld-select" value={filters.followUp} onChange={(e) => setFilter("followUp", e.target.value)}>
            <option value="">Any</option>
            <option value="overdue">Overdue</option>
            <option value="today">Due today</option>
            <option value="upcoming">Later</option>
            <option value="none">None set</option>
          </select>
        </div>
        <div className="ld-filter" style={{ minWidth: 140 }}>
          <span className="ld-filter-label">Automation</span>
          <select className="ld-select" value={filters.automation} onChange={(e) => setFilter("automation", e.target.value)}>
            <option value="">Any</option><option value="on">On</option><option value="off">Off</option>
          </select>
        </div>
        <div className="ld-filter" style={{ minWidth: 190 }}>
          <span className="ld-filter-label">Messages</span>
          <select className="ld-select" value={filters.messages} onChange={(e) => setFilter("messages", e.target.value)} title="Failed: not sent, or sent and reported not delivered">
            <option value="">Any</option>
            <option value="failed">Has a failed message</option>
          </select>
        </div>
        <div className="ld-filter" style={{ minWidth: 150 }}>
          <span className="ld-filter-label">Created from</span>
          <input type="date" className="ld-input" value={filters.from} onChange={(e) => setFilter("from", e.target.value)} />
        </div>
        <div className="ld-filter" style={{ minWidth: 150 }}>
          <span className="ld-filter-label">to</span>
          <input type="date" className="ld-input" value={filters.to} onChange={(e) => setFilter("to", e.target.value)} />
        </div>
        <div className="ld-filter" style={{ minWidth: 170 }}>
          <span className="ld-filter-label">Sort</span>
          <select className="ld-select" value={filters.sort || "newest"} onChange={(e) => setFilter("sort", e.target.value === "newest" ? "" : e.target.value)}>
            <option value="newest">Newest first</option>
            <option value="followUp">Follow-up soonest</option>
            <option value="activity">Recent activity</option>
            <option value="oldest">Oldest first</option>
          </select>
        </div>
        {anyFilter && <button type="button" className="ld-btn" onClick={() => { setSearch(""); setParams(new URLSearchParams()); }}>Clear filters</button>}
      </div>

      {error && <div className="ld-alert ld-alert-error">{error}</div>}

      {selected.size > 0 && (
        <div className="ld-bulk-bar">
          <b>{selected.size.toLocaleString("en-IN")} selected</b>
          {selected.size < total && (
            <button type="button" className="ld-link-btn" disabled={selectingAll} onClick={selectAllMatching}>
              {selectingAll ? "Selecting…" : `Select all ${Math.min(total, 5000).toLocaleString("en-IN")} matching`}
            </button>
          )}
          <span className="ld-bulk-bar-actions">
            <button type="button" className="ld-btn ld-btn-sm ld-btn-primary" onClick={() => setBulk("status")}>Change status…</button>
            <button type="button" className="ld-btn ld-btn-sm" onClick={() => setBulk("message")}>Send notification…</button>
            <button type="button" className="ld-btn ld-btn-sm" onClick={() => setSelected(new Map())}>Clear</button>
          </span>
        </div>
      )}

      <div className="ld-card">
        {rows.length > 0 && (
          <div className="ld-table-tools">
            <button type="button" className="ld-btn ld-btn-sm" onClick={toggleAll} title="Show every lead's notifications on this page">
              {allOpen ? "▴ Collapse all" : `▾ Expand all (${rows.length})`}
            </button>
          </div>
        )}
        <div className="ld-table-wrap">
          <table className="ld-table">
            <thead>
              <tr><th className="ld-check" onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label="Select every lead on this page" checked={pageSelected} onChange={togglePage} /></th><th aria-label="Notifications" /><th>Lead</th><th>Course / campaign</th><th>Status</th><th>Notifications</th><th>Demo</th><th>Follow-up</th><th>Last activity</th><th>Created</th><th>Auto</th></tr>
            </thead>
            <tbody>
              {rows.map((l) => {
                const overdue = l.followUpAt && isOverdue(l.followUpAt) && !["registered", "not_interested", "not_qualified"].includes(l.status);
                const isOpen = open.has(l._id);
                const m = l.messages || {};
                return (
                  <React.Fragment key={l._id}>
                  <tr className={[isOpen && "ld-row-open", selected.has(l._id) && "ld-row-selected"].filter(Boolean).join(" ") || undefined} onClick={() => navigate(`/leads/${l._id}${listQuery ? `?${listQuery}` : ""}`)}>
                    <td className="ld-check" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label={`Select ${l.fullName}`} checked={selected.has(l._id)} onChange={() => toggleSelect(l)} />
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="ld-expand" aria-expanded={isOpen} title={isOpen ? "Hide notifications" : "Show notifications: sent and coming up"} onClick={() => toggle(l._id)}>{isOpen ? "▾" : "▸"}</button>
                    </td>
                    <td>
                      <div className="ld-name">{l.fullName}{l.meta?.leadgenId && <span className="ld-chip ld-chip-meta" title={l.meta.adName ? `Ad: ${l.meta.adName}` : "Meta Lead Ads"}>{l.meta.platform === "ig" ? "Instagram" : l.meta.platform === "fb" ? "Facebook" : "Meta"}</span>}</div>
                      <div className="ld-sub">{l.mobile || "no number"}{l.city ? ` · ${l.city}` : ""}{l.stage ? ` · ${l.stage}` : ""}</div>
                    </td>
                    <td><div>{l.courseTitle}</div><div className="ld-sub">{l.campaignName}</div></td>
                    <td><StatusPill status={l.status} label={l.statusLabel} group={l.statusGroup} /></td>
                    <td className="ld-msg-sum" onClick={(e) => { e.stopPropagation(); toggle(l._id); }} style={{ cursor: "pointer" }} title="Show notifications">
                      <div><b>{m.sent || 0}</b> sent{m.failed ? <span style={{ color: "var(--ld-red)" }}> · {m.failed} failed</span> : ""}{m.upcoming ? <> · <b>{m.upcoming}</b> to come</> : ""}</div>
                      <div className="ld-sub">{m.next ? <>Next: {m.next.title} · {MESSAGE_CHANNEL[m.next.channel] || m.next.channel} · {fmtDateTime(m.next.dueAt)}{m.next.status === "paused" ? " (paused)" : ""}</> : "Nothing scheduled"}</div>
                    </td>
                    <td className="ld-sub">{l.demoSession ? fmtDateTime(l.demoSession.startsAt) : l.meta?.leadgenId ? "Unassigned" : "—"}</td>
                    <td>{l.followUpAt ? (<><div className={`ld-due${overdue ? " is-overdue" : ""}`}>{fmtDue(l.followUpAt)}</div><div className="ld-sub">{fmtDateTime(l.followUpAt)}</div></>) : <span className="ld-sub">—</span>}</td>
                    <td><div className="ld-sub" style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.lastActivity}>{l.lastActivity || "—"}</div><div className="ld-sub">{fmtDateTime(l.lastActivityAt)}</div></td>
                    <td className="ld-sub">{fmtDateTime(l.createdAt)}</td>
                    <td className="ld-sub">{l.automationOn ? "On" : "Off"}</td>
                  </tr>
                  {isOpen && (
                    <tr className="ld-row-detail"><td colSpan={11}><LeadMessagesPanel leadId={l._id} to={`/leads/${l._id}${listQuery ? `?${listQuery}` : ""}`} data={messages[l._id]?.error ? null : messages[l._id]} error={messages[l._id]?.error} /></td></tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {!rows.length && <div className="ld-empty">{loading ? "Loading…" : anyFilter ? "No leads match these filters." : "No leads yet."}</div>}
        </div>
        {total > 0 && (
          <div className="ld-pager">
            <span>{(page - 1) * rowsPerPage + 1}–{Math.min(page * rowsPerPage, total)} of {total.toLocaleString("en-IN")} · Page {page} of {pages}</span>
            <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label className="ld-sub" htmlFor="ld-rows">Rows per page</label>
              <select id="ld-rows" className="ld-select" style={{ width: "auto" }} value={rowsPerPage} onChange={(e) => changeRows(Number(e.target.value))}>
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <button type="button" className="ld-btn ld-btn-sm" disabled={page <= 1} onClick={() => goTo(page - 1)}>Previous</button>
              <button type="button" className="ld-btn ld-btn-sm" disabled={page >= pages} onClick={() => goTo(page + 1)}>Next</button>
            </span>
          </div>
        )}
      </div>

      {bulk === "status" && meta && (
        <BulkStatusDialog leads={selectedLeads} meta={meta} onClose={(changed) => { setBulk(null); if (changed) { setSelected(new Map()); load(); } }} />
      )}
      {bulk === "message" && (
        <BulkMessageDialog leads={selectedLeads} onClose={(sent) => { setBulk(null); if (sent) { setSelected(new Map()); load(); } }} />
      )}

      {adding && (
        <div className="ld-overlay" onMouseDown={(e) => e.target === e.currentTarget && setAdding(false)}>
          <div className="ld-modal" role="dialog" aria-modal="true" aria-label="Add lead">
            <div className="ld-modal-head"><h3>Add lead</h3><button type="button" className="ld-btn ld-btn-sm" onClick={() => setAdding(false)}>Close</button></div>
            <div className="ld-modal-body">
              {!campaigns.length ? (
                <div className="ld-alert ld-alert-info">Create a campaign first (<Link to="/leads/setup">Lead settings → Campaigns</Link>).</div>
              ) : (
                <LeadForm
                  campaigns={campaigns.filter((c) => c.isActive)}
                  submitLabel="Add lead"
                  onCancel={() => setAdding(false)}
                  onSubmit={async (form) => {
                    const r = await leadApi.create(form);
                    setAdding(false);
                    navigate(`/leads/${r.data._id}`);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadList;
